import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { Coords, Time } from '../../../../../../common/types'
import { ButtonCategory } from '../../../../../../components/Button'
import GetGeolocation from '../../../../../../components/Geolocation'
import { RouteColumn, RouteRow, RouteWrapper } from '../../../../../../components/layout/'
import { TextInput } from '../../../../../../components/layout/form/input'
import { LocationInputMap, toMapData } from '../../../../../../components/layout/form/input/LocationInputMap'
import { CustomButton, StationButtonGroup } from '../../../../../../components/StationButtonGroup'

import { GeoLocateAddressData } from '../../types'
import { RouteHeader } from '../RouteHeader'
import { ArrivalRouteArgs, ArrivalAddress, EditStartRouteFormValues } from '../StartRoute/types'
import { AutoliittoSeparatorLine } from '../styled'
import { useApolloClient } from '@apollo/client/react'
import { setErrorNotification } from '../../../../../../components/notification'
import { GeolocationType, Geolocation } from '../../../../../../components/Geolocation/types'
import { ArrivalDateTimeInput, DateTimeInput } from '../../../../../../components/layout/form/input/DateTimeInput'
import { format } from 'date-fns'
import { RouteNotes } from '../../RouteNotes'
import { StyledButton } from '../../../../../../components/ReverseGeocodeButton'
import { Maybe } from '../../../../../../common/Maybe'
import styled from 'styled-components'
import { RouteColumnAlignRight } from '../../../../../../components/layout/routesmenu'
import { Station } from '../../../../../Parameters/types'
import { useEstimateArrivalTime } from '../../../../../../components/layout/form/field/TimeField'
import { routeSubformMessages } from '../messages'

export interface LocationOption {
  value: string
  label: string
}
export const startLocationOther = 'OTHER'

interface StartRouteProps {
  onCancel: () => void
  regNo: string
  isAutoliitto: boolean
  isSos: boolean
  useExtendedRoutes: boolean
  setAutoliittoDateField: (fieldName: string) => void
  setRouteLocation: (addressData: GeoLocateAddressData | null) => void
  updateRouteDate?: () => void
  //showCustomAddressInitially: boolean
  initialValues: EditStartRouteFormValues
  clear: () => void
  setGeolocatedField: (geolocated: boolean) => void
  setLoading: (loading: boolean) => void
  reportPosition: (location: Geolocation, name: string) => void
  getValue: any
  setValue: (field: string, value: any) => void
  stationId: Maybe<number>
  stations: Station[]
}

export const StartRoute: React.FunctionComponent<StartRouteProps> = ({
  onCancel,
  isAutoliitto,
  regNo,
  setAutoliittoDateField,
  setRouteLocation,
  initialValues,
  clear,
  setGeolocatedField,
  setLoading,
  isSos,
  reportPosition,
  getValue,
  setValue,
  stationId,
  stations,
}) => {
  const intl = useIntl()
  const selectedBtnCategory: ButtonCategory = 'yellow'
  const unSelectedBtnCategory: ButtonCategory = 'new'

  const routeAddress = getStartRouteAddress(initialValues)
  const showStations = stations.length > 0
  const selectedStation = stations.find(station => `${station.address}, ${station.city}` === routeAddress) || null
  const selection = getInitialStationSelection(stations, selectedStation, routeAddress)

  const selectOption = (selectedOption: string) => {
    setCleared(false)
    setSelectedOption(selectedOption)
  }

  const [showCustomAddress, setShowCustomAddress] = useState<boolean>(routeAddress !== '')
  const [selectedOption, setSelectedOption] = useState<string>(selection)
  
  const [selectedStationId, setStation] = useState<number | null>(() => {
    return selectedStation ? selectedStation.id : null
  })
  const [cleared, setCleared] = useState<boolean>(false)

  const client = useApolloClient()

  const destination = arrivalRouteToQueryVariable({
    address: getValue(`arrivalRoute.address`) as string,
    city: getValue(`arrivalRoute.city`) as string,
    coords: getValue(`arrivalRoute.coords`) as Coords,
  })

  const { getPosition, loading } = useEstimateArrivalTime(destination, {
    onSuccess: (time, date) => {
      setValue('automobileAndTouringClubFinland.estimatedTimeOfArrival.time', time)
      setValue('automobileAndTouringClubFinland.estimatedTimeOfArrival.date', date)
    },
    onError: error => {
      console.log(error)
    },
  })

  return (
    <RouteWrapper>
      <RouteHeader label={intl.formatMessage(routeSubformMessages.startLocation)} regNo={regNo} cancel={onCancel} />
      <>
        <AutoliittoSeparatorLine marginTop="1.5rem" marginBottom="1.5rem" />
        <RouteRow columns={showStations ? '1fr 1fr 1fr' : '1fr 1fr'}>
          {showStations && (
            <RouteColumn>
              <CustomButton
                label={intl.formatMessage(routeSubformMessages.station)}
                category={selectedOption && selectedOption === 'station' ? selectedBtnCategory : unSelectedBtnCategory}
                onClick={() => {
                  selectOption('station')
                  setGeolocatedField(false)
                }}
                size="ml"
                maxWidth="100%"
              />
            </RouteColumn>
          )}
          <RouteColumn>
            <CustomButton
              label={intl.formatMessage(routeSubformMessages.other)}
              category={selectedOption && selectedOption === 'manual' ? selectedBtnCategory : unSelectedBtnCategory}
              onClick={() => {
                setShowCustomAddress(true)
                selectOption('manual')
                setGeolocatedField(false)
              }}
              size="ml"
              maxWidth="100%"
            />
          </RouteColumn>
        </RouteRow>
      </>

      <AutoliittoSeparatorLine marginTop="1.5rem" marginBottom="1.5rem" />
      <RouteRow>
        <RouteColumn>
          {showCustomAddress && selectedOption !== 'station' && (
            <RouteColumn>
              <LocationInputMap
                addressLabel={intl.formatMessage(routeSubformMessages.address)}
                addressName={`startRoute.address`}
                cityLabel={intl.formatMessage(routeSubformMessages.city)}
                cityName={`startRoute.city`}
                coordsName={`startRoute.coords`}
                zipcodeName={`startRoute.zipcode`}
                required={isSos || isAutoliitto}
                coordsRequired={isSos}
                getValue={getValue}
                values={toMapData(
                  getValue(`startRoute.address`),
                  getValue(`startRoute.city`),
                  getValue(`startRoute.coords`),
                  getValue(`startRoute.zipcode`)
                )}
                setValue={setValue}
                setRouteLocation={setRouteLocation}
                stationId={stationId as number} // Fix
                clear={() => {
                  clear()
                  setStation(null)
                }}
              />
            </RouteColumn>
          )}
          {selectedOption === 'station' && (
            <StationButtonGroup
              stations={stations}
              label={intl.formatMessage(routeSubformMessages.startRouteStationGroup)}
              setRouteLocation={setRouteLocation}
              stationId={selectedStationId}
              setStation={stationId => {
                setStation(stationId)
              }}
              cleared={cleared}
              setLoading={setLoading}
              required={isSos || isAutoliitto}
            />
          )}
        </RouteColumn>
        <RouteColumn>
          <ArrivalDateTimeInput
            label={intl.formatMessage(routeSubformMessages.time)}
            name="startRoute"
            required={isAutoliitto}
            customOnChange={() => {
              const value = (format(new Date(), 'HH:mm') as unknown) as Time
              setAutoliittoDateField('startRoute.arrivalDate')
              setValue('startRoute.arrivalTime', value)
            }}
            showCurrentTimeBtn
          />
        </RouteColumn>
      </RouteRow>

      {isAutoliitto && (
        <>
          <AutoliittoSeparatorLine marginTop="1.5rem" marginBottom="1.5rem" />
          <RouteRow>
            <RouteColumn>
              <TextInput disabled label={intl.formatMessage(routeSubformMessages.destinationAddress)} name={`arrivalRoute.address`} />
            </RouteColumn>
            <RouteColumn>
              <DateTimeInput
                label={intl.formatMessage(routeSubformMessages.estimatedArrivalTime)}
                name={'automobileAndTouringClubFinland.estimatedTimeOfArrival'}
                customOnChange={() => {
                  const value = (format(new Date(), 'HH:mm') as unknown) as Time
                  setAutoliittoDateField('automobileAndTouringClubFinland.estimatedTimeOfArrival.date')
                  setValue('automobileAndTouringClubFinland.estimatedTimeOfArrival.time', value)
                }}
                required={isAutoliitto}
                showCurrentTimeBtn={false}
              />
            </RouteColumn>
          </RouteRow>
          <RouteRow>
            <RouteColumn />
            <RouteColumnAlignRight>
              <CalculateButton
                label={intl.formatMessage(routeSubformMessages.calculate)}
                category="info"
                loading={loading}
                onClick={() => getPosition()}
                fontWeight="bold"
                maxWidth="50%"
                size="s"
              />
            </RouteColumnAlignRight>
          </RouteRow>
        </>
      )}

      {isSos && (
        <>
          <GetGeolocation
            {...({
              client,
              setError: setErrorNotification,
              type: GeolocationType.reverseGeocode,
              reportGeolocation: reportPosition,
              fieldName: 'sosServiceOrder.currentLocation',
            } as any)}
          />
          <AutoliittoSeparatorLine marginTop="1.5rem" marginBottom="1.5rem" />
          <RouteRow>
            <RouteColumn>
              <LocationInputMap
                addressLabel={intl.formatMessage(routeSubformMessages.unitLocation)}
                addressName={`sosServiceOrder.currentLocation.address`}
                cityLabel={intl.formatMessage(routeSubformMessages.city)}
                cityName={`sosServiceOrder.currentLocation.city`}
                coordsName={`sosServiceOrder.currentLocation.coords`}
                zipcodeName={`sosServiceOrder.currentLocation.zipcode`}
                required={isSos}
                coordsRequired={isSos}
                getValue={getValue}
                values={toMapData(
                  getValue(`sosServiceOrder.currentLocation.address`),
                  getValue(`sosServiceOrder.currentLocation.city`),
                  getValue(`sosServiceOrder.currentLocation.coords`),
                  getValue(`sosServiceOrder.currentLocation.zipcode`)
                )}
                setValue={setValue}
                stationId={stationId as number}
                clear={clear}
              />
            </RouteColumn>
            <RouteColumn>
              <DateTimeInput
                label={intl.formatMessage(routeSubformMessages.startTime)}
                name="sosServiceOrder.jobStarted"
                required={isSos}
                customOnChange={() => {
                  const value = (format(new Date(), 'HH:mm') as unknown) as Time
                  setAutoliittoDateField('sosServiceOrder.jobStarted.date')
                  setValue('sosServiceOrder.jobStarted.time', value)
                }}
                showCurrentTimeBtn
              />
            </RouteColumn>
          </RouteRow>
          <RouteRow></RouteRow>
        </>
      )}
      <RouteNotes removeLine />
    </RouteWrapper>
  )
}

const CalculateButton = styled(StyledButton as any)``

export function arrivalRouteToQueryVariable(arrivalRoute: Maybe<ArrivalAddress>): ArrivalRouteArgs {
  if (!arrivalRoute) {
    return {
      address: '',
      city: '',
      lat: null,
      long: null,
    }
  }

  const coords = arrivalRoute.coords ? arrivalRoute.coords : { lat: null, long: null }

  return {
    address: arrivalRoute.address,
    city: arrivalRoute.city ? arrivalRoute.city : '',
    lat: coords.lat,
    long: coords.long,
  }
}

function getInitialStationSelection(stations: Station[], selectedStation: Maybe<Station>, routeAddress: string) {
  const selection = stations.length > 0 ? 'station' : 'manual'

  if (selectedStation) {
    return 'station'
  }

  if (!selectedStation && routeAddress) {
    return 'manual'
  }

  return selection
}

function getStartRouteAddress({ startRoute }: EditStartRouteFormValues) {
  if (!startRoute.address || !startRoute.city) return ''

  return `${startRoute.address}, ${startRoute.city}`
}
